import React from 'react';
import { X } from 'lucide-react';
import type { LscPublishConfirm } from '../types';
import { PublishConfirmFacts } from './PublishConfirmModal';

interface NeedsAttentionActionConfirmModalProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  /** Renders the confirm button in a destructive (red) style for removals. */
  destructive?: boolean;
  showNote?: boolean;
  note?: string;
  onNoteChange?: (value: string) => void;
  confirm?: LscPublishConfirm | null;
  testId?: string;
}

/**
 * Final confirmation gate for high-impact Needs Attention actions (remove,
 * post to archive, save & post). Copy is passed in from the caller so this
 * stays commissioner-safe and never renders item internals.
 */
const NeedsAttentionActionConfirmModal: React.FC<NeedsAttentionActionConfirmModalProps> = ({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  busy = false,
  destructive = false,
  showNote = false,
  note = '',
  onNoteChange,
  confirm,
  testId = 'needs-attention-action-confirm-modal',
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="needs-attention-action-confirm-title"
      data-testid={testId}
    >
      <div className="card max-w-md w-full relative">
        <button
          type="button"
          onClick={onCancel}
          className="absolute top-4 right-4 text-secondary-400 hover:text-white"
          aria-label="Close"
          disabled={busy}
        >
          <X className="h-5 w-5" />
        </button>
        <h2
          id="needs-attention-action-confirm-title"
          className="text-lg font-semibold text-white mb-2"
        >
          {title}
        </h2>
        <p className="text-sm text-secondary-400 mb-4">{body}</p>
        <PublishConfirmFacts confirm={confirm} />
        {showNote ? (
          <label className="flex flex-col gap-1 text-sm mb-4">
            <span className="text-secondary-400">Note (optional)</span>
            <textarea
              className="input-field min-h-[60px]"
              value={note}
              onChange={(event) => onNoteChange?.(event.target.value)}
              disabled={busy}
              placeholder="Optional note for the archive record"
              data-testid="needs-attention-publish-note"
            />
          </label>
        ) : null}
        <div className="flex gap-3 justify-end">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={
              destructive
                ? 'btn-secondary text-red-300 border-red-800 hover:bg-red-900/30'
                : 'btn-primary'
            }
            onClick={onConfirm}
            disabled={busy}
            data-testid="needs-attention-action-confirm-button"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NeedsAttentionActionConfirmModal;
